import { NfcCard } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useCallback, useMemo, useState } from 'react';
import {
  isCardBlockedStatus,
  isCardExpiredStatus,
  isCardExpiresSoonStatus,
  isRightsChangedStatus,
  isUserDeletedStatus,
  isUserExpiresSoonStatus
} from 'utils/NfcCard/nfcCard';
import { filterStatusDefault } from 'v2/filters/accessCards/cardFilterConstants';
import {
  AccessCardEntry,
  AccessCardsFilterState,
  CardStatus,
  FilterAssignedState,
  FilterStatusState,
  FilterValidity,
  FilterValidityState
} from 'v2/filters/accessCards/cardFilterTypes';
import { createCardFilterChips } from 'v2/filters/accessCards/createCardFilterChips';
import { createCardFilterSections } from 'v2/filters/accessCards/createCardFilterSections';
import { filterCards } from 'v2/filters/accessCards/filterCards';
import { useAccessCardSearch } from 'v2/filters/accessCards/useAccessCardSearch';
import { parseEnumParam, parseParamToBooleanRecord, serializeEnum } from 'v2/filters/filterUrlParams';
import { hasEnabledFilter } from 'v2/filters/hasEnabledFilter';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';

const resolveCardStatuses = (card: NfcCard): CardStatus[] => {
  const statuses: [boolean, CardStatus][] = [
    [isCardBlockedStatus(card), 'cardBlocked'],
    [isUserDeletedStatus(card), 'userDeleted'],
    [isCardExpiredStatus(card), 'cardExpired'],
    [isRightsChangedStatus(card), 'accessRightsChanged'],
    [isUserExpiresSoonStatus(card), 'userExpiresSoon'],
    [isCardExpiresSoonStatus(card), 'cardExpiresSoon']
  ];

  return statuses.filter(([isActive]) => isActive).map(([, status]) => status);
};

const toEntry = (card: NfcCard): AccessCardEntry => ({
  id: card.uuid,
  uuid: card.uuid,
  name: card.name ?? null,
  visitorName: card.assignment?.visitorName ?? '',
  visitorEmail: card.assignment?.visitor?.email ?? null,
  expiryDate: card.assignment?.expiryDate ?? null,
  expired: Boolean(card.assignment?.expired),
  assigned: Boolean(card.assignment),
  blocked: Boolean(card.assignment?.blocked),
  blockedSince: card.assignment?.blocked?.since ?? null,
  blockedReason: card.assignment?.blocked?.reason ?? null,
  statuses: resolveCardStatuses(card)
});

interface Params {
  accessCards: NfcCard[];
}

type AccessCardFilterParam = 'search' | 'status' | 'validity' | 'assigned';

const ACCESS_CARD_FILTER_PARAM_KEYS: AccessCardFilterParam[] = ['search', 'status', 'validity', 'assigned'];

const FILTER_VALIDITY_VALUES = Object.values(FilterValidity);
const FILTER_ASSIGNED_VALUES = Object.values(FilterAssignedState);

export const useAccessCardsSearchAndFilters = ({ accessCards }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams, updateMultiSelectFilter } =
    useFilterSearchParams<AccessCardFilterParam>(ACCESS_CARD_FILTER_PARAM_KEYS);

  // Not part of the shared filter system (no chip, excluded from clearAllFilters/isAnyFilterEnabled),
  // so it stays as local UI state rather than a URL param.
  const [hideBlocked, setHideBlocked] = useState(false);

  const searchParam = searchParams.get('search');
  const statusParam = searchParams.get('status');
  const validityParam = searchParams.get('validity');
  const assignedParam = searchParams.get('assigned');

  const filterStr = searchParam ?? '';
  const filterStatus = useMemo(() => parseParamToBooleanRecord(statusParam, filterStatusDefault), [statusParam]);
  const filterValidity = useMemo(() => parseEnumParam(validityParam, FILTER_VALIDITY_VALUES), [validityParam]);
  const filterAssigned = useMemo(() => parseEnumParam(assignedParam, FILTER_ASSIGNED_VALUES), [assignedParam]);

  const setFilterStr = useCallback(
    (value: string) => updateFilterParams(() => ({ search: value === '' ? undefined : value })),
    [updateFilterParams]
  );

  const setFilterStatus = useCallback(
    (value: FilterStatusState | ((current: FilterStatusState) => FilterStatusState)) =>
      updateMultiSelectFilter('status', filterStatusDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterValidity = useCallback(
    (value: FilterValidityState) => updateFilterParams(() => ({ validity: serializeEnum(value) })),
    [updateFilterParams]
  );

  const setFilterAssigned = useCallback(
    (value: FilterAssignedState) => updateFilterParams(() => ({ assigned: serializeEnum(value) })),
    [updateFilterParams]
  );

  const clearAllFilters = useCallback(() => {
    updateFilterParams(() => ({
      search: undefined,
      status: undefined,
      validity: undefined,
      assigned: undefined
    }));
  }, [updateFilterParams]);

  const isAnyFilterEnabled = useMemo(
    () => hasEnabledFilter(filterStr, filterStatus, filterValidity, filterAssigned),
    [filterStr, filterStatus, filterValidity, filterAssigned]
  );

  const filters: AccessCardsFilterState = useMemo(
    () => ({
      filter: { filterStatus, filterValidity, filterAssigned },
      setters: { setFilterStatus, setFilterValidity, setFilterAssigned },
      clearAllFilters,
      isAnyFilterEnabled
    }),
    [
      filterStatus,
      filterValidity,
      filterAssigned,
      setFilterStatus,
      setFilterValidity,
      setFilterAssigned,
      clearAllFilters,
      isAnyFilterEnabled
    ]
  );

  const entries = useMemo(() => accessCards.map(toEntry), [accessCards]);

  const searchedEntries = useAccessCardSearch({ entries, search: filterStr, translation });

  const filteredCards = useMemo(
    () => filterCards({ entries: searchedEntries, filterStatus, filterValidity, filterAssigned, hideBlocked }),
    [searchedEntries, filterStatus, filterValidity, filterAssigned, hideBlocked]
  );

  const countCaption = `${filteredCards.length} ${translation.of} ${accessCards.length} ${translation.access_cards}`;

  const sections = useMemo(() => createCardFilterSections({ translation, filters }), [translation, filters]);
  const chips = useMemo(() => createCardFilterChips({ translation, filters }), [translation, filters]);

  return {
    filterStr,
    setFilterStr,
    hideBlocked,
    setHideBlocked,
    filteredCards,
    sections,
    chips,
    countCaption,
    isAnyFilterEnabled,
    clearAllFilters
  };
};
